import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  CardContent,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Box
} from "@mui/material";

import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";

import "./certificate-page.css";

import certificates from "./certificate-data";

const CertificatePage = () => {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");

  // ✅ NEW: gallery vs carousel
  const [viewMode, setViewMode] = useState("carousel"); // "gallery" | "carousel"

  // ✅ NEW: carousel state
  const [activeIndex, setActiveIndex] = useState(0);

  const handleOpen = (type, links) => {
    if (type === "codepathT" || type === "codepathW") {
      setSelectedFile(links);
      setOpen(true);
    } else {
      window.open(links, "_blank", "noopener,noreferrer");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedFile("");
  };

  // ✅ NEW: helpers for carousel
  const total = certificates.length;

  const clampIndex = (i) => {
    if (total === 0) return 0;
    return (i + total) % total;
  };

  const goPrev = () => setActiveIndex((i) => clampIndex(i - 1));
  const goNext = () => setActiveIndex((i) => clampIndex(i + 1));

  const currentCert = useMemo(() => certificates[activeIndex], [activeIndex]);

  // ✅ NEW: keyboard arrows for carousel
  useEffect(() => {
    if (viewMode !== "carousel") return;

    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  return (
    <div className="page-body-certificates" id="certificates-block">
      <div className="certificate-introduction">
        <h1>
          <b>Certificates and Bootcamps</b>
        </h1>
        <p>
          Here is a list of my accomplished certificates through courses, tranings and bootcamps. Through those,
          I had chances to sharpen my skills in Software Development and broaden my horizons. Please view in your desktop for best experiene!!
        </p>

        {/* ✅ NEW: view toggle (doesn't change your existing classes) */}
        <div className="certificate-view-toggle">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="carousel">Carousel</ToggleButton>
            <ToggleButton value="gallery">Gallery</ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>

      <div className="certificates-showing">
        {/* ✅ NEW: CAROUSEL (added only) */}
        {viewMode === "carousel" && total > 0 && (
          <div className="certificate-carousel">
            <IconButton className="certificate-carousel-btn left" onClick={goPrev} aria-label="Previous certificate">
              <ArrowBackIosNewRoundedIcon />
            </IconButton>

            <div className="certificate-carousel-stage">
              <Card className="certificate-carousel-card" sx={{ borderRadius: "16px", padding: "12px" }}>
                <CardActionArea onClick={() => handleOpen(currentCert.type, currentCert.links)}>
                  <CardMedia
                    component="img"
                    height="220"
                    image={currentCert.imageLink}
                    alt={`${currentCert.source} certificate`}
                    className="certificate-carousel-image"
                  />
                  <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                      {currentCert.source}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: "500" }}>
                      {currentCert.name}
                    </Typography>
                  </CardContent>
                </CardActionArea>

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, pb: 1 }}>
                  <Button variant="outlined" onClick={() => handleOpen(currentCert.type, currentCert.links)}>
                    View Certificate
                  </Button>

                  <Typography variant="body2" sx={{ opacity: 0.75, fontWeight: 600 }}>
                    {activeIndex + 1} / {total}
                  </Typography>
                </Stack>
              </Card>

              {/* dots */}
              <Box className="certificate-carousel-dots" aria-label="Certificate carousel dots">
                {certificates.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`certificate-carousel-dot ${i === activeIndex ? "active" : ""}`}
                    onClick={() => setActiveIndex(i)}
                    aria-label={`Go to certificate ${i + 1}`}
                  />
                ))}
              </Box>
            </div>

            <IconButton className="certificate-carousel-btn right" onClick={goNext} aria-label="Next certificate">
              <ArrowForwardIosRoundedIcon />
            </IconButton>
          </div>
        )}

        {/* ✅ KEEP YOUR CURRENT GALLERY EXACTLY */}
        {viewMode === "gallery" && (
          <Grid container spacing={2}>
            {certificates.map((cert, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ borderRadius: "12px", padding: "10px" }}>
                  <CardActionArea>
                    <CardMedia component="img" height="100" image={cert.imageLink} alt="Company Images" />
                    <CardContent>
                      <Typography gutterBottom variant="h5" component="div" sx={{ fontWeight: "bold" }}>
                        {cert.source}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: "500" }}>
                        {cert.name}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                  <Button variant="outlined" onClick={() => handleOpen(cert.type, cert.links)}>
                    View Certificate
                  </Button>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
          <DialogContent>
            {selectedFile.endsWith(".pdf") && (
              <iframe
                src={`${selectedFile}#zoom=70`}
                width="100%"
                height="700px"
                title="Certificate Preview"
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CertificatePage;
